<script setup>
import SiteLayout from '@/Layouts/Layout.vue';
import TextInput from '@/Components/Input/TextInput.vue';
import LookupSelect from '@/Pages/Lookup/Partials/LookupSelect.vue';
import PrimaryButton from '@/Components/Button/PrimaryButton.vue';
import { reactive } from 'vue';
import Card from '@/Components/Card/Card.vue';
import CardLabel from '@/Components/Card/CardLabel.vue';
import KnownSelectors from '@/Pages/Explain/Partials/KnownSelectors.vue';
import { router } from '@inertiajs/vue3';
import Alert from '@/Components/Alert.vue';
import CardDetailNs from '@/Pages/Lookup/Partials/Dns/CardDetailNs.vue';
import { TOOL_LOOKUP, captureToolUsed } from '@/Common/Posthog.js';

const props = defineProps({
  domain: {
    type: String,
    default: ''
  },
  selector: {
    type: String,
    default: ''
  },
  result: {
    type: Object,
    default: null
  },
  ns: {
    type: Object,
    default: null
  }
});

const state = reactive({
  domain: props.domain,
  selector: props.selector,
  isLoading: false
});

if (props.result) {
  captureToolUsed({
    tool: TOOL_LOOKUP,
    properties: {
      tested_domain: props.domain,
      lookup_type: 'dkim'
    }
  });
}

const submit = () => {
  state.isLoading = true;
  router.visit(route('explain.dkim', [state.domain, state.selector]));
};
</script>

<template>
  <SiteLayout :title="$t('explain.dkim.dkim_lookup')">
    <Card>
      <form @submit.prevent="submit">
        <div class="flex flex-col gap-6 md:flex-row">
          <div class="md:w-3/12">
            <LookupSelect selected="DKIM" />
          </div>
          <TextInput
            v-model.trim="state.selector"
            class="flex-1"
            size="large"
            :label="$t('explain.dkim.selector')"
            :description="$t('explain.dkim.selector_description')"
            clearable
          />
          <TextInput
            v-model.trim="state.domain"
            class="flex-1"
            size="large"
            :label="$t('explain.dkim.domain_name')"
            :description="$t('explain.dkim.domain_description')"
            clearable
            autofocus
            required
          />
          <div class="flex md:items-center md:justify-center">
            <PrimaryButton
              size="large"
              :is-loading="state.isLoading"
              class="mt-2"
            >
              {{ $t('explain.dkim.lookup') }}
            </PrimaryButton>
          </div>
        </div>
      </form>
    </Card>

    <Card v-if="result && result.hasOwnProperty('detail')">
      <Alert variant="danger" :title="$t('explain.dkim.record_not_found')">
        {{ result.detail }}
      </Alert>
    </Card>

    <div v-else-if="result && !selector">
      <CardLabel :label="$t('explain.dkim.known_selectors')" />
      <Card>
        <KnownSelectors :domain="domain" :selectors="result.records" />
      </Card>
    </div>

    <div v-else-if="result && selector">
      <CardLabel
        :label="
          $t('explain.dkim.dkim_selector_for', {
            selector: selector,
            domain: domain
          })
        "
      />
      <Card>
        <Alert variant="success" class="mb-6">
          <div class="text-lg">{{ $t('explain.dkim.dkim_record_found') }}</div>
        </Alert>
        <KnownSelectors :domain="domain" :selectors="[result]" expanded />
      </Card>
    </div>

    <div>
      <CardLabel :label="$t('explain.dkim.name_servers')" />
      <Card>
        <CardDetailNs :lookup="ns" />
      </Card>
    </div>
  </SiteLayout>
</template>
